import React, { useCallback, useMemo, useState } from 'react';
import { dateSelection, periods, sections, BALANCE_ACTIONS, balanceSections } from './data';
import { actions as assistantActions } from 'features/assistant';
import { useDispatch } from 'react-redux';

const initialCurrentDate = {
  from: dateSelection.from,
  to: dateSelection.to,
  value: dateSelection.value,
  active: false,
};

const useControlPanel = () => {
  const dispatch = useDispatch();

  const [inputClarificationValue, setInputClarificationValue] = useState('');
  const [currentSections, changeCurrentSections] = useState(sections);
  const [currentPeriods, changeCurrentPeriods] = useState(periods);
  const [currentSubsections, changeCurrentSubsections] = useState([]);
  const [currentDate, selectDate] = useState(initialCurrentDate);
  const [parsedUsername, setParsedUsername] = useState(null);
  const [isEmptyParseResult, setIsEmptyParseResult] = useState(false);

  const clearParseState = useCallback(() => {
    setParsedUsername(null);
    setIsEmptyParseResult(false);
  }, []);

  const selectedSection = useMemo(() => currentSections.find(s => s.active) ?? null, [currentSections]);
  const selectedPeriod = useMemo(() => currentPeriods.find(s => s.active) ?? null, [currentPeriods]);
  const selectedSubsection = useMemo(() => currentSubsections.find(s => s.active) ?? null, [currentSubsections]);

  const isSectionSelected = !!selectedSection;
  const isPeriodSelected = !!selectedPeriod;
  const isSubsectionSelected = !!selectedSubsection;

  const isBalanceMode = useMemo(
    () => !!selectedSection && BALANCE_ACTIONS.includes(selectedSection.value),
    [selectedSection],
  );

  const isBackButtonActive = isPeriodSelected || isSectionSelected || isSubsectionSelected || isEmptyParseResult;
  const isTableShown = isBalanceMode
    ? isSectionSelected
    : isPeriodSelected && isSectionSelected && isSubsectionSelected;

  const fullContext = useMemo(
    () => [selectedSection, selectedPeriod, selectedSubsection].filter(Boolean).map(el => ({ ...el, key: el.key })),
    [selectedPeriod, selectedSection, selectedSubsection],
  );

  const handleSelectCurrentSection = useCallback(
    value => {
      clearParseState();
      changeCurrentSections(prev => {
        const idx = prev.findIndex(s => s.value === value);
        if (idx === -1) return prev;

        const next = [...prev];
        const nextActive = !next[idx].active;
        next[idx] = { ...next[idx], active: nextActive };

        const children = nextActive ? next[idx].children ?? [] : [];
        changeCurrentSubsections(children.map(ch => ({ ...ch, active: false })));

        return next;
      });
    },
    [clearParseState],
  );

  const handleSelectCurrentPeriod = useCallback(
    value => {
      clearParseState();
      changeCurrentPeriods(prev => {
        const idx = prev.findIndex(p => p.value === value);
        if (idx === -1) return prev;

        const next = [...prev];
        next[idx] = { ...next[idx], active: !next[idx].active };
        return next;
      });
    },
    [clearParseState],
  );

  const handleSelectCurrentSubsection = useCallback(
    value => {
      clearParseState();
      changeCurrentSubsections(prev => {
        const idx = prev.findIndex(p => p.value === value);
        if (idx === -1) return prev;

        const next = [...prev];
        next[idx] = { ...next[idx], active: !next[idx].active };
        return next;
      });
    },
    [clearParseState],
  );

  const handleSelectFromDate = value => {
    clearParseState();
    selectDate(prev => ({ ...prev, from: value }));
    if (!!currentDate.to && !!value) handleSelectCurrentPeriod(currentDate.value);
  };

  const handleSelectToDate = value => {
    clearParseState();
    selectDate(prev => ({ ...prev, to: value }));
    if (!!value && !!currentDate.from) handleSelectCurrentPeriod(currentDate.value);
  };

  const handleOpenDate = () => {
    selectDate(prev => ({ ...prev, active: !prev.active }));
  };

  const handleInputClarificationValue = value => {
    setInputClarificationValue(value);
  };

  const clearCurrentDate = () => {
    selectDate(initialCurrentDate);
  };

  const clearCurrentPeriods = () => {
    changeCurrentPeriods(periods);
  };

  const clearCurrentSections = () => {
    changeCurrentSections(sections);
  };

  const clearCurrentSubsections = (reset = false) => {
    if (reset) changeCurrentSubsections([]);
    else {
      const newSubsections = currentSubsections.map(ss => ({ ...ss, active: false }));
      changeCurrentSubsections(newSubsections);
      dispatch(assistantActions.clearTableData());
    }
  };

  const resetPanel = useCallback(() => {
    changeCurrentSections(sections);
    changeCurrentPeriods(periods);
    changeCurrentSubsections([]);
    selectDate(initialCurrentDate);
    setParsedUsername(null);
    setIsEmptyParseResult(false);
  }, []);

  const applyParsedResult = useCallback(parsed => {
    if (!parsed) return false;
    const { action, date_from, date_to, username } = parsed;

    if (!action) {
      setParsedUsername(null);
      setIsEmptyParseResult(true);
      changeCurrentSections(sections);
      changeCurrentPeriods(periods);
      changeCurrentSubsections([]);
      selectDate(initialCurrentDate);
      return true;
    }

    if (BALANCE_ACTIONS.includes(action)) {
      setParsedUsername(username ?? null);
      setIsEmptyParseResult(false);

      changeCurrentSections([{ ...balanceSections[action], active: true }]);
      changeCurrentSubsections([]);

      const fromDate = date_from ? new Date(date_from) : null;
      const toDate = date_to ? new Date(date_to) : null;

      if (fromDate && toDate) {
        selectDate({ from: fromDate, to: toDate, value: dateSelection.value, active: false });
        changeCurrentPeriods(periods.map(p => ({ ...p, active: p.value === dateSelection.value })));
      } else {
        selectDate(initialCurrentDate);
        changeCurrentPeriods(periods);
      }

      return true;
    }

    const matchedSection = sections.find(s => s.children?.some(c => c.value === action));
    if (!matchedSection) return false;

    setParsedUsername(username ?? null);
    setIsEmptyParseResult(false);

    changeCurrentSections(sections.map(s => ({ ...s, active: s.value === matchedSection.value })));
    changeCurrentSubsections(matchedSection.children.map(c => ({ ...c, active: c.value === action })));

    const fromDate = date_from ? new Date(date_from) : null;
    const toDate = date_to ? new Date(date_to) : null;
    selectDate({
      from: fromDate,
      to: toDate,
      value: dateSelection.value,
      active: false,
    });
    changeCurrentPeriods(periods.map(p => ({ ...p, active: p.value === dateSelection.value })));

    return true;
  }, []);

  const backToPreviousSection = () => {
    if (!isBackButtonActive) return;
    if (isBalanceMode) {
      resetPanel();
      dispatch(assistantActions.clearTableData());
      return;
    }
    clearParseState();
    clearCurrentDate();
    switch (true) {
      case isSubsectionSelected:
        clearCurrentSubsections();
        return;
      case isPeriodSelected:
        clearCurrentPeriods();
        return;
      case isSectionSelected:
        clearCurrentSections();
        clearCurrentSubsections(true);
        return;
      default:
        return;
    }
  };

  return {
    inputState: {
      currentSections,
      currentPeriods,
      currentSubsections,
      handleSelectCurrentPeriod,
      handleSelectCurrentSection,
      handleSelectCurrentSubsection,
      handleInputClarificationValue,
      currentDate,
      handleSelectFromDate,
      handleSelectToDate,
      handleOpenDate,
      backToPreviousSection,
      applyParsedResult,
      resetPanel,
    },
    outputState: {
      fullContext,
      isTableShown,
      isBackButtonActive,
      isSectionSelected,
      isPeriodSelected,
      isSubsectionSelected,
      isBalanceMode,
      selectedSection,
      selectedPeriod,
      selectedSubsection,
      inputClarificationValue,
      parsedUsername,
      isEmptyParseResult,
    },
  };
};

export default useControlPanel;
